import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation, getReturnUrl } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
// import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors"; //Store Custom Code
import signInAsGuest from "@insite/client-framework/Store/Pages/SignIn/Handlers/SignInAsGuest";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyProps } from "@insite/mobius/Typography/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

/* Custom Code - Start */
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import constants from "@store/constants";
import mergeProps from "@store/Framework/Common/Utilities/mergeProps";
import { useIsNamedContext } from "@store/Framework/Components/NamedContext";
import PersonAddLine from "@store/Library/Components/Icons/PersonAdd/PersonAddLine";
import FormActions from "@store/Library/Components/Toolkit/FormActions";
import TextSeparator, { TextSeparatorPresentationProps } from "@store/Library/Components/Toolkit/TextSeparator";
import { signInProps } from "@store/Themes/common/props/signIn";
import parse from "html-react-parser";
/* Custom Code - End */

/* Store Custom Code - Start */
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { getPageLinkByPageType, useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
/* Store Custom Code - End */

const enum fields {
    text = "text",
    fullWidthButton = "fullWidthButton",
    // Custom Code
    title = "title",
    showIcon = "showIcon",
    // Store Custom Code
    requestPage = "requestPage",
}

const mapStateToProps = (state: ApplicationState) => {
    let { search } = getLocation(state);
    const { allowCreateAccount, allowGuestCheckout } = getSettingsCollection(state).accountSettings;
    let returnUrl = getReturnUrl(state);
    if (!returnUrl && state.components.signInOverlay.isOpen) {
        returnUrl = getLocation(state).pathname;
        if (!search) {
            search = `?returnUrl=${returnUrl}`;
        }
    }

    const shippingPageUrl = getPageLinkByPageType(state, "CheckoutShippingPage")?.url;
    const referredFromShipping = returnUrl === shippingPageUrl;
    const onePageCheckoutPageUrl = getPageLinkByPageType(state, "OnePageCheckoutPage")?.url;
    const referredFromOnePageCheckout = returnUrl === onePageCheckoutPageUrl;
    const createAccountPageLink = getPageLinkByPageType(state, "CreateAccountRequestPage"); // Store Custom Code
    return {
        allowCreateAccount,
        allowGuestCheckout:
            allowGuestCheckout &&
            !getCurrentUserIsGuest(state) &&
            (referredFromShipping || referredFromOnePageCheckout),
        returnUrl,
        createAccountUrl: createAccountPageLink ? `${createAccountPageLink.url}${search}` : undefined,
    };
};

const mapDispatchToProps = {
    signInAsGuest,
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.text]: string;
        [fields.fullWidthButton]: boolean;
        // Custom Code
        [fields.showIcon]: boolean;
        [fields.title]: string;
        // Store Custom Code
        [fields.requestPage]: LinkFieldValue;
    };
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface SignInCreateNewAccountStyles {
    signInCreateNewAccountGridContainer?: GridContainerProps;
    signInCreateNewAccountTitleGridItem?: GridItemProps;
    signInCreateNewAccountTextGridItem?: GridItemProps;
    signInCreateNewAccountButtonGridItem?: GridItemProps;
    signInCreateNewAccountTitle?: TypographyProps;
    signInCreateNewAccountText?: TypographyProps;
    checkoutAsGuestButton?: ButtonPresentationProps;
    createNewAccountButton?: ButtonPresentationProps;
    // Custom Code
    signInCreateNewAccountIcon?: IconPresentationProps;
    signInCreateNewAccountWrapper?: InjectableCss;
    separator?: TextSeparatorPresentationProps;
}

export const signInCreateNewAccountStyles: SignInCreateNewAccountStyles = {
    signInCreateNewAccountWrapper: signInProps.wrapper,
    signInCreateNewAccountIcon: {
        ...signInProps.icon,
        src: PersonAddLine,
    },
    signInCreateNewAccountTitle: signInProps.title,
    createNewAccountButton: { variant: "tertiary" },
    separator: signInProps.separator,
};

/**
 * @deprecated Use signInCreateNewAccountStyles instead.
 */
export const signInCreateNewAccount = signInCreateNewAccountStyles;
const styles = signInCreateNewAccountStyles;

const SignInCreateNewAccount: FC<Props> = ({
    history,
    fields: { fullWidthButton, showIcon, title, text, requestPage }, // Store Custom Code
    allowCreateAccount,
    allowGuestCheckout,
    returnUrl,
    createAccountUrl,
    signInAsGuest,
}) => {
    /* Store Custom Code - Start */
    const link = useGetLink(requestPage);
    const { isInShell } = useShellContext();
    /* Store Custom Code - End */

    /* Custom Code - Start */
    const isSignInOverlay = useIsNamedContext(constants.pageType.signInOverlay);
    const styles = React.useMemo(() => {
        if (!isSignInOverlay) {
            return signInCreateNewAccountStyles;
        }

        return mergeProps<SignInCreateNewAccountStyles>(signInCreateNewAccountStyles, {
            signInCreateNewAccountWrapper: signInProps.overlayOverrides?.wrapper,
            signInCreateNewAccountIcon: signInProps?.overlayOverrides?.icon,
            signInCreateNewAccountTitle: signInProps?.overlayOverrides?.title,
        });
    }, [isSignInOverlay]);
    /* Custom Code - End */

    if (!allowCreateAccount) {
        return null;
    }

    const handleCheckoutAsGuest = () => {
        if (!returnUrl) {
            return;
        }

        signInAsGuest({ returnUrl });
    };

    const handleCreateNewAccount = () => {
        /* Store Custom Code - Start */
        if (!link.url) {
            return;
        }

        if (link.url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = link.url;
        } else {
            history.push(link.url);
        }
        /* Store Custom Code - End */
    };

    // Removed - createButtonCss

    return (
        <>
            {/* Custom Code - Start */}
            {isSignInOverlay && <TextSeparator {...styles.separator}>{translate("OR")}</TextSeparator>}
            {/* Custom Code - End */}
            <StyledWrapper {...styles.signInCreateNewAccountWrapper} /* Custom Code */>
                {/* Custom Code - Start */}
                {showIcon && <Icon {...styles.signInCreateNewAccountIcon} />}
                {title && <Typography {...styles.signInCreateNewAccountTitle}>{title}</Typography>}
                {text && <Typography {...styles.signInCreateNewAccountText}>{parse(text, parserOptions)}</Typography>}
                {/* Custom Code - End */}
                {/* Removed Code - AllowGuestCheckout Button */}
                {
                    /* Store Custom Code */ link.url && (
                        <FormActions fullWidth={fullWidthButton} /* Custom Code */>
                            <Button
                                {...styles.createNewAccountButton}
                                // Removed - createButtonCss
                                onClick={handleCreateNewAccount}
                                data-test-selector="signInCreateNewAccount_createNewAccount"
                            >
                                {translate("Account Creation Request") /* Store Custom Code */}
                            </Button>
                        </FormActions>
                    )
                }
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withHistory(connect(mapStateToProps, mapDispatchToProps)(SignInCreateNewAccount)),
    definition: {
        allowedContexts: ["SignInPage", "SignInOverlay"],
        group: "Sign In",
        icon: "User",
        fieldDefinitions: [
            /* Custom Code - Start */
            {
                name: fields.showIcon,
                displayName: "Show Icon",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                /* Store Custom Code - Start */
                defaultValue: "Web Account Creation Request",
                /* Store Custom Code - End */
                fieldType: "Translatable",
            },
            /* Custom Code - End */
            {
                name: fields.text,
                displayName: "Content", // Custom Code
                editorTemplate: "RichTextField", // Custom Code
                /* Store Custom Code - Start */
                defaultValue:
                    "<p>Want to open an account with SPI ?</p><p>Please contact our customer service at 1-855-774-7227 or reach us with the contact form to open an account.</p>",
                /* Store Custom Code - End */
                fieldType: "Translatable",
                // Removed - sortOrder
            },
            {
                name: fields.fullWidthButton,
                displayName: "Full Width Button",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                // Removed - sortOrder
            },
            /* Store Custom Code - Start */
            {
                name: fields.requestPage,
                displayName: "Account Creation Request Page",
                editorTemplate: "LinkField",
                fieldType: "Translatable",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                isRequired: true,
            },
            /* Store Custom Code - End */
        ],
    },
};

export default widgetModule;
